import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { CDSButton } from "@ciscodesignsystems/cds-react-button";
import { CDSCard } from "@ciscodesignsystems/cds-react-card";
import { CDSContainer } from "@ciscodesignsystems/cds-react-container";
import { CDSFlex } from "@ciscodesignsystems/cds-react-flex";
import { CDSModal } from "@ciscodesignsystems/cds-react-modal";
import { CDSNotification } from "@ciscodesignsystems/cds-react-notification";
import { CDSSkeleton } from "@ciscodesignsystems/cds-react-skeleton";
import { CDSTag } from "@ciscodesignsystems/cds-react-tag";
import { CDSTextArea } from "@ciscodesignsystems/cds-react-textarea";
import { CDSTextInput } from "@ciscodesignsystems/cds-react-text-input";
import { fixSpec, reviewSpec, uploadSpecToScg } from "../services/specReviewService";

const sampleSpecification = `openapi: 3.1.0
info:
  title: "API name (title)"
  description: ""
  version: "1.0.0"
servers:
  - url: "https://api.security.cisco.com/v1"
security:
  - bearerHttpAuthentication: []
tags:
  - name: "Organizations"
  - name: "Subscriptions"
  - name: "Users"
  - name: "Roles"
  - name: "AdminGroups"
paths:
  "/organizations":
    get:
      summary: "List organizations"
      operationId: "getOrganizations"
      tags:
        - "Organizations"
      parameters:
        - name: cursor
          in: query
          required: false
          schema:
            type: string
      responses:
        "200":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"
    post:
      summary: "Create organizations"
      operationId: "postOrganizations"
      tags:
        - "Organizations"
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
      responses:
        "201":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"

  "/subscriptions":
    get:
      summary: "List subscriptions"
      operationId: "getSubscriptions"
      tags:
        - "Subscriptions"
      parameters:
        - name: cursor
          in: query
          required: false
          schema:
            type: string
      responses:
        "200":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"
    post:
      summary: "Create subscriptions"
      operationId: "postSubscriptions"
      tags:
        - "Subscriptions"
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
      responses:
        "201":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"

  "/users":
    get:
      summary: "List users"
      operationId: "getUsers"
      tags:
        - "Users"
      parameters:
        - name: cursor
          in: query
          required: false
          schema:
            type: string
      responses:
        "200":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"
    post:
      summary: "Create users"
      operationId: "postUsers"
      tags:
        - "Users"
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
      responses:
        "201":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"

  "/roles":
    get:
      summary: "List roles"
      operationId: "getRoles"
      tags:
        - "Roles"
      parameters:
        - name: cursor
          in: query
          required: false
          schema:
            type: string
      responses:
        "200":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"
    post:
      summary: "Create roles"
      operationId: "postRoles"
      tags:
        - "Roles"
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
      responses:
        "201":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"

  "/admin-groups":
    get:
      summary: "List admin groups"
      operationId: "getAdminGroups"
      tags:
        - "AdminGroups"
      parameters:
        - name: cursor
          in: query
          required: false
          schema:
            type: string
      responses:
        "200":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"
    post:
      summary: "Create admin groups"
      operationId: "postAdminGroups"
      tags:
        - "AdminGroups"
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
      responses:
        "201":
          description: "Successful response"
          headers:
            Content-Type:
              $ref: "#/components/headers/ContentType"
            trackingId:
              $ref: "#/components/headers/TrackingId"
          content:
            application/json:
              schema:
                type: object
                properties:
                  items:
                    type: array
                    items:
                      type: object
        "400":
          $ref: "#/components/responses/BadRequest"
        "401":
          $ref: "#/components/responses/Unauthorized"
        "500":
          $ref: "#/components/responses/InternalServerError"

components:
  headers:
    ContentType:
      description: Response media type
      schema:
        type: string
    TrackingId:
      description: Request correlation identifier
      schema:
        type: string
  responses:
    BadRequest:
      description: Invalid request
    Unauthorized:
      description: Unauthorized
    InternalServerError:
      description: Internal server error
  securitySchemes:
    bearerHttpAuthentication:
      type: http
      scheme: bearer
    oauth2ClientCredentials:
      type: oauth2
      flows:
        clientCredentials:
          tokenUrl: "https://api.security.cisco.com/oauth/token"
          scopes: {}`;

const severityLabelMap = {
  error: "ERROR",
  warning: "WARNING",
  info: "INFO",
};

const severityClassMap = {
  error: "uag-review-finding--error",
  warning: "uag-review-finding--warning",
  info: "uag-review-finding--info",
};

const REVIEW_SPEC_DELAY_MS = process.env.NODE_ENV === "test" ? 0 : 5000;

const editorLineHeight = 24;
const reviewSpecStorageKey = "uag.reviewSpecPage";

const getDefaultSource = (fileName) => (fileName ? `upload://${fileName}` : "manual://review-spec");
const getEditorElement = (editorShellRef) => editorShellRef?.querySelector?.("textarea") || null;
const getLineNumberFromSelection = (value, selectionStart) =>
  value.slice(0, Math.max(selectionStart, 0)).split("\n").length;

const createTextSegment = (value, cursor) => ({
  type: "text",
  key: `text-${cursor}`,
  value,
});
const createReviewNotification = ({ status, title, description }) => ({
  status,
  title,
  description,
});
const normalizeReviewErrorMessage = (error) => {
  const message = String(error?.message || "").trim();

  if (!message || message === "Failed to fetch") {
    return "Failed to review the specification. Check your connection and try again.";
  }

  return message;
};

const jsonTitlePattern = /"title"\s*:\s*"([^"]+)"/;
const yamlTitlePrefixPattern = /^[ \t]*title:[ \t]*/;
const yamlWrappingQuotePattern = /^['"]|['"]$/g;
const maxStructuredDiffCells = 120000;

const extractServiceName = (openapiText) => {
  const specification = String(openapiText || "");
  const jsonTitleMatch = jsonTitlePattern.exec(specification);

  if (jsonTitleMatch?.[1]) {
    return jsonTitleMatch[1].replace(/\s+/g, "").trim();
  }

  const titleLine = specification
    .split("\n")
    .find((line) => yamlTitlePrefixPattern.test(line));

  if (titleLine) {
    const yamlTitleValue = titleLine
      .replace(yamlTitlePrefixPattern, "")
      .trim()
      .replace(yamlWrappingQuotePattern, "");

    if (yamlTitleValue) {
      return yamlTitleValue.replace(/\s+/g, "").trim();
    }
  }

  return "";
};

const getLinkCandidate = (text, labelStart) => {
  const labelEnd = text.indexOf("]", labelStart + 1);
  const openParen = labelEnd === -1 ? -1 : text.indexOf("(", labelEnd + 1);
  const closeParen = openParen === -1 ? -1 : text.indexOf(")", openParen + 1);

  if (!(labelEnd > labelStart + 1 && openParen === labelEnd + 1 && closeParen > openParen + 1)) {
    return null;
  }

  return {
    labelEnd,
    closeParen,
    url: text.slice(openParen + 1, closeParen),
  };
};

const isSupportedLinkUrl = (url) => url.startsWith("http://") || url.startsWith("https://");

export const parseLinkedText = (value) => {
  const text = String(value || "");
  const segments = [];
  let cursor = 0;

  while (cursor < text.length) {
    const labelStart = text.indexOf("[", cursor);

    if (labelStart === -1) {
      segments.push(createTextSegment(text.slice(cursor), cursor));
      break;
    }

    const linkCandidate = getLinkCandidate(text, labelStart);

    if (!linkCandidate || !isSupportedLinkUrl(linkCandidate.url)) {
      segments.push(createTextSegment(text.slice(cursor), cursor));
      break;
    }

    if (labelStart > cursor) {
      segments.push(createTextSegment(text.slice(cursor, labelStart), cursor));
    }

    segments.push({
      type: "link",
      key: `link-${labelStart}-${linkCandidate.url}`,
      label: text.slice(labelStart + 1, linkCandidate.labelEnd),
      url: linkCandidate.url,
    });

    cursor = linkCandidate.closeParen + 1;
  }

  if (!segments.length) {
    return [createTextSegment(text, 0)];
  }

  return segments;
};

const renderLinkedText = (value) => {
  return parseLinkedText(value).map((segment) => {
    if (segment.type === "text") {
      return <Fragment key={segment.key}>{segment.value}</Fragment>;
    }

    return (
      <a
        key={segment.key}
        className="uag-review-reference-link"
        href={segment.url}
        target="_blank"
        rel="noreferrer"
      >
        {segment.label}
      </a>
    );
  });
};

const buildFindingClassName = (finding, isInteractiveFinding) =>
  [
    "uag-review-finding",
    severityClassMap[finding.severity],
    isInteractiveFinding ? "uag-review-finding--interactive" : "",
  ]
    .filter(Boolean)
    .join(" ");

const renderSuggestedFixContent = (suggestedFix) => {
  if (!suggestedFix) {
    return null;
  }

  return (
    <p>
      <strong>Suggested fix:</strong> {renderLinkedText(suggestedFix)}
    </p>
  );
};

const renderFindingCard = (finding, jumpToLine) => {
  const isInteractiveFinding = Boolean(finding.line);
  const findingKey = finding.id || `${finding.title}-${finding.line || "no-line"}`;
  const findingClassName = buildFindingClassName(finding, isInteractiveFinding);
  const findingSummary = (
    <CDSFlex direction="vertical" gap="xs">
      <CDSFlex className="uag-review-finding-header" gap="sm" align="center">
        <CDSTag>{severityLabelMap[finding.severity]}</CDSTag>
        <strong>{finding.title}</strong>
      </CDSFlex>
      <p>{finding.description}</p>
    </CDSFlex>
  );
  const suggestedFixContent = renderSuggestedFixContent(finding.suggestedFix);

  if (!isInteractiveFinding) {
    return (
      <div key={findingKey} className={findingClassName}>
        <CDSFlex direction="vertical" gap="xs">
          {findingSummary}
          {suggestedFixContent}
        </CDSFlex>
      </div>
    );
  }

  return (
    <div key={findingKey} className={findingClassName}>
      <button
        type="button"
        className="uag-review-finding-action"
        aria-label={`${severityLabelMap[finding.severity]} ${finding.title}`}
        onClick={() => jumpToLine(finding.line)}
      >
        {findingSummary}
      </button>
      {suggestedFixContent}
    </div>
  );
};

const getFindingsContent = ({
  isSubmitting,
  visibleFindings,
  findings,
  emptyStateMessage,
  jumpToLine,
}) => {
  if (isSubmitting) {
    return (
      <div className="uag-review-loading-block" aria-label="Findings loading">
        <CDSSkeleton height="lg" lines={4} shade="regular" />
        <CDSSkeleton height="lg" lines={4} shade="regular" />
      </div>
    );
  }

  if (visibleFindings.length) {
    return visibleFindings.map((finding) => renderFindingCard(finding, jumpToLine));
  }

  return (
    <div className="uag-review-empty-state">
      {findings.length ? emptyStateMessage : "Run a review to populate findings."}
    </div>
  );
};

const getDiffMarker = (entryType) => {
  if (entryType === "add") {
    return "+";
  }

  if (entryType === "remove") {
    return "-";
  }

  return " ";
};

const renderFixDiffPane = ({ fixDiff, hasFixDiffChanges, onDismiss }) => {
  if (!fixDiff) {
    return null;
  }

  return (
    <div className="uag-review-diff-pane">
      <div className="uag-review-diff-header">
        <div>
          <h2>Applied fixes diff</h2>
          <p>
            {hasFixDiffChanges
              ? `+${fixDiff.additions} additions, -${fixDiff.removals} removals`
              : "No text changes were returned by the fix API."}
            {fixDiff.mode === "fallback"
              ? " Large specs use a simplified before/after comparison."
              : ""}
          </p>
        </div>
        <CDSButton variant="secondary" onClick={onDismiss}>
          Dismiss diff
        </CDSButton>
      </div>

      <div className="uag-review-diff-shell" aria-label="Applied fixes diff">
        {fixDiff.entries.map((entry, index) => (
          <div
            key={`${entry.type}-${entry.beforeLineNumber || "na"}-${entry.afterLineNumber || "na"}-${index}`}
            className={`uag-review-diff-row uag-review-diff-row--${entry.type}`}
          >
            <span className="uag-review-diff-line-number">{entry.beforeLineNumber || ""}</span>
            <span className="uag-review-diff-line-number">{entry.afterLineNumber || ""}</span>
            <span className="uag-review-diff-marker" aria-hidden="true">
              {getDiffMarker(entry.type)}
            </span>
            <code>{entry.value || " "}</code>
          </div>
        ))}
      </div>
    </div>
  );
};

const renderReviewNotification = (reviewNotification) => {
  if (!reviewNotification) {
    return null;
  }

  return (
    <CDSNotification
      className="uag-review-notification"
      status={reviewNotification.status}
      title={reviewNotification.title}
    >
      {reviewNotification.description}
    </CDSNotification>
  );
};

const renderSubmittingOverlay = (isSubmitting) => {
  if (!isSubmitting) {
    return null;
  }

  return (
    <div className="uag-onboard-loading-overlay">
      <div className="uag-onboard-loading-panel">
        <div className="uag-onboard-spinner" aria-hidden="true" />
        <output className="uag-onboard-loading-status" aria-live="polite">
          Reviewing spec
        </output>
        <p>Reviewing the OpenAPI specification and preparing the findings summary.</p>
      </div>
    </div>
  );
};
const buildReviewSuccessNotification = ({ summary, findings }) => {
  const hasIssues = Boolean(summary.total || findings.length);

  if (!hasIssues) {
    return createReviewNotification({
      status: "positive",
      title: "Review completed",
      description: "This is a success message",
    });
  }

  const summaryParts = [
    `${summary.total} total`,
    summary.errors ? `${summary.errors} errors` : null,
    summary.warnings ? `${summary.warnings} warnings` : null,
    summary.info ? `${summary.info} info` : null,
  ].filter(Boolean);

  return createReviewNotification({
    status: "warning",
    title: "Review completed with issues",
    description: `Results: ${summaryParts.join(", ")}.`,
  });
};

const getStoredReviewState = (windowObject) => {
  if (!windowObject) {
    return null;
  }

  try {
    const storedValue = windowObject.localStorage?.getItem(reviewSpecStorageKey);

    if (!storedValue) {
      return null;
    }

    const parsedValue = JSON.parse(storedValue);

    if (!parsedValue || typeof parsedValue !== "object") {
      return null;
    }

    return {
      specificationText:
        typeof parsedValue.specificationText === "string"
          ? parsedValue.specificationText
          : "",
      cleanReviewedSpecificationText:
        typeof parsedValue.cleanReviewedSpecificationText === "string"
          ? parsedValue.cleanReviewedSpecificationText
          : "",
      source:
        typeof parsedValue.source === "string" ? parsedValue.source : "manual://review-spec",
      searchValue: typeof parsedValue.searchValue === "string" ? parsedValue.searchValue : "",
      reviewSummary:
        parsedValue.reviewSummary && typeof parsedValue.reviewSummary === "object"
          ? {
              total: Number(parsedValue.reviewSummary.total || 0),
              errors: Number(parsedValue.reviewSummary.errors || 0),
              warnings: Number(parsedValue.reviewSummary.warnings || 0),
              info: Number(parsedValue.reviewSummary.info || 0),
            }
          : {
              total: 0,
              errors: 0,
              warnings: 0,
              info: 0,
            },
      findings: Array.isArray(parsedValue.findings) ? parsedValue.findings : [],
      activeLine: Number(parsedValue.activeLine || 0),
    };
  } catch (error) {
    return null;
  }
};

const getInitialReviewState = () =>
  typeof window === "undefined" ? null : getStoredReviewState(window);

const persistReviewState = (windowObject, state) => {
  if (!windowObject) {
    return;
  }

  try {
    windowObject.localStorage?.setItem(reviewSpecStorageKey, JSON.stringify(state));
  } catch (error) {
    // Ignore storage failures (quota/private mode) so editing and review flows continue.
  }
};

const parseReviewStorageEvent = (event) => {
  if (event.key !== reviewSpecStorageKey || !event.newValue) {
    return null;
  }

  try {
    const nextState = JSON.parse(event.newValue);

    return {
      specificationText: nextState.specificationText || "",
      cleanReviewedSpecificationText: nextState.cleanReviewedSpecificationText || "",
      source: nextState.source || "manual://review-spec",
      searchValue: nextState.searchValue || "",
      reviewSummary:
        nextState.reviewSummary || {
          total: 0,
          errors: 0,
          warnings: 0,
          info: 0,
        },
      findings: Array.isArray(nextState.findings) ? nextState.findings : [],
      activeLine: Number(nextState.activeLine || 0),
    };
  } catch (error) {
    return null;
  }
};

const createDiffLine = (type, value, beforeLineNumber = null, afterLineNumber = null) => ({
  type,
  value,
  beforeLineNumber,
  afterLineNumber,
});

const buildSimpleLineDiff = (beforeText, afterText) => {
  const beforeLines = String(beforeText || "").split("\n");
  const afterLines = String(afterText || "").split("\n");
  const maxLength = Math.max(beforeLines.length, afterLines.length);
  const entries = [];
  let additions = 0;
  let removals = 0;

  for (let index = 0; index < maxLength; index += 1) {
    const beforeLine = beforeLines[index];
    const afterLine = afterLines[index];
    const lineNumber = index + 1;

    if (beforeLine === afterLine) {
      entries.push(createDiffLine("context", beforeLine || "", lineNumber, lineNumber));
      continue;
    }

    if (typeof beforeLine === "string") {
      removals += 1;
      entries.push(createDiffLine("remove", beforeLine, lineNumber, null));
    }

    if (typeof afterLine === "string") {
      additions += 1;
      entries.push(createDiffLine("add", afterLine, null, lineNumber));
    }
  }

  return {
    entries,
    additions,
    removals,
    mode: "fallback",
  };
};

const buildStructuredLineDiff = (beforeText, afterText) => {
  const beforeLines = String(beforeText || "").split("\n");
  const afterLines = String(afterText || "").split("\n");
  const lcs = Array.from({ length: beforeLines.length + 1 }, () =>
    Array(afterLines.length + 1).fill(0)
  );

  for (let beforeIndex = beforeLines.length - 1; beforeIndex >= 0; beforeIndex -= 1) {
    for (let afterIndex = afterLines.length - 1; afterIndex >= 0; afterIndex -= 1) {
      lcs[beforeIndex][afterIndex] =
        beforeLines[beforeIndex] === afterLines[afterIndex]
          ? lcs[beforeIndex + 1][afterIndex + 1] + 1
          : Math.max(lcs[beforeIndex + 1][afterIndex], lcs[beforeIndex][afterIndex + 1]);
    }
  }

  const entries = [];
  let beforeIndex = 0;
  let afterIndex = 0;
  let additions = 0;
  let removals = 0;

  while (beforeIndex < beforeLines.length && afterIndex < afterLines.length) {
    if (beforeLines[beforeIndex] === afterLines[afterIndex]) {
      entries.push(
        createDiffLine("context", beforeLines[beforeIndex], beforeIndex + 1, afterIndex + 1)
      );
      beforeIndex += 1;
      afterIndex += 1;
      continue;
    }

    if (lcs[beforeIndex + 1][afterIndex] >= lcs[beforeIndex][afterIndex + 1]) {
      removals += 1;
      entries.push(createDiffLine("remove", beforeLines[beforeIndex], beforeIndex + 1, null));
      beforeIndex += 1;
      continue;
    }

    additions += 1;
    entries.push(createDiffLine("add", afterLines[afterIndex], null, afterIndex + 1));
    afterIndex += 1;
  }

  while (beforeIndex < beforeLines.length) {
    removals += 1;
    entries.push(createDiffLine("remove", beforeLines[beforeIndex], beforeIndex + 1, null));
    beforeIndex += 1;
  }

  while (afterIndex < afterLines.length) {
    additions += 1;
    entries.push(createDiffLine("add", afterLines[afterIndex], null, afterIndex + 1));
    afterIndex += 1;
  }

  return {
    entries,
    additions,
    removals,
    mode: "structured",
  };
};

const buildSpecDiff = (beforeText, afterText) => {
  const beforeLines = String(beforeText || "").split("\n");
  const afterLines = String(afterText || "").split("\n");

  if (beforeLines.length * afterLines.length > maxStructuredDiffCells) {
    return buildSimpleLineDiff(beforeText, afterText);
  }

  return buildStructuredLineDiff(beforeText, afterText);
};

const ReviewSpecPage = () => {
  const [initialState] = useState(() => getInitialReviewState());
  const fileInputRef = useRef(null);
  const editorShellRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const [specificationText, setSpecificationText] = useState(initialState?.specificationText || "");
  const [cleanReviewedSpecificationText, setCleanReviewedSpecificationText] = useState(
    initialState?.cleanReviewedSpecificationText || ""
  );
  const [source, setSource] = useState(initialState?.source || "manual://review-spec");
  const [searchValue, setSearchValue] = useState(initialState?.searchValue || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApplyingFixes, setIsApplyingFixes] = useState(false);
  const [isUploadingToScg, setIsUploadingToScg] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(
    initialState?.reviewSummary || {
      total: 0,
      errors: 0,
      warnings: 0,
      info: 0,
    }
  );
  const [reviewNotification, setReviewNotification] = useState(null);
  const [findings, setFindings] = useState(initialState?.findings || []);
  const [activeLine, setActiveLine] = useState(initialState?.activeLine || 0);
  const [scgUploadResult, setScgUploadResult] = useState(null);
  const [fixDiff, setFixDiff] = useState(null);

  const visibleFindings = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();

    if (!normalizedQuery) {
      return findings;
    }

    return findings.filter((finding) =>
      [
        finding.title,
        finding.description,
        finding.suggestedFix,
        ...(finding.references || []).flatMap((reference) => [reference.title, reference.link]),
        String(finding.line || ""),
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [findings, searchValue]);

  const specificationLines = useMemo(() => {
    const normalizedText = specificationText || "";
    return normalizedText.split("\n").length;
  }, [specificationText]);

  const emptyStateMessage = findings.length
    ? "No findings match the current search."
    : "Run a review to populate findings.";
  const canApplyFixes = findings.length > 0 && !isSubmitting && !isApplyingFixes;
  const serviceName = extractServiceName(specificationText);
  const canUploadToScg =
    Boolean(specificationText.trim()) &&
    Boolean(serviceName) &&
    !isSubmitting &&
    !isApplyingFixes &&
    !isUploadingToScg;
  const hasFixDiffChanges = Boolean((fixDiff?.additions || 0) + (fixDiff?.removals || 0));
  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleSpecChange = (event) => {
    setSpecificationText(event.target.value);
    setCleanReviewedSpecificationText("");
    setReviewNotification(null);
    setFixDiff(null);
  };

  const syncEditorScroll = (editorElement) => {
    const nextScrollTop = editorElement?.scrollTop || 0;

    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = nextScrollTop;
    }

  };

  const handleSpecScroll = (event) => {
    syncEditorScroll(event.target);
  };

  const syncActiveLineFromSelection = (editorElement) => {
    setActiveLine(getLineNumberFromSelection(editorElement.value || "", editorElement.selectionStart || 0));
  };

  const jumpToLine = (line) => {
    const editorElement = getEditorElement(editorShellRef.current);

    if (!line || !editorElement) {
      return;
    }

    const numericLine = Number(line);

    if (!Number.isFinite(numericLine) || numericLine < 1) {
      return;
    }

    const targetTop = Math.max((numericLine - 1) * editorLineHeight, 0);
    const lines = (editorElement.value || "").split("\n");
    const targetIndex = Math.max(
      lines.slice(0, Math.max(numericLine - 1, 0)).join("\n").length + (numericLine > 1 ? 1 : 0),
      0
    );
    const targetLineText = lines[Math.max(numericLine - 1, 0)] || "";
    const targetLineEnd = targetIndex + targetLineText.length;

    editorElement.focus();
    editorElement.setSelectionRange(targetIndex, targetLineEnd);
    editorElement.scrollTop = targetTop;
    syncActiveLineFromSelection(editorElement);

    window.requestAnimationFrame(() => {
      syncEditorScroll(editorElement);
    });
  };

  const handleUpload = async (event) => {
    const [file] = event.target.files || [];

    if (!file) {
      return;
    }

    const text = await file.text();
    setSpecificationText(text);
    setSource(getDefaultSource(file.name));
    setCleanReviewedSpecificationText("");
    setReviewNotification(null);
    setFixDiff(null);
    event.target.value = "";
  };

  const handleClear = () => {
    setSpecificationText("");
    setSource("manual://review-spec");
    setSearchValue("");
    setFindings([]);
    setActiveLine(0);
    setCleanReviewedSpecificationText("");
    setReviewSummary({
      total: 0,
      errors: 0,
      warnings: 0,
      info: 0,
    });
    setReviewNotification(null);
    setFixDiff(null);
  };

  const handleReview = async () => {
    if (!specificationText.trim()) {
      const message = "Paste or upload an OpenAPI specification before running a review.";
      setReviewNotification(
        createReviewNotification({
          status: "negative",
          title: "Review request failed",
          description: message,
        })
      );
      return;
    }

    setIsSubmitting(true);
    setReviewNotification(null);
    setFindings([]);
    setActiveLine(0);
    setCleanReviewedSpecificationText("");
    setFixDiff(null);
    setReviewSummary({
      total: 0,
      errors: 0,
      warnings: 0,
      info: 0,
    });

    try {
      await new Promise((resolve) => {
        window.setTimeout(resolve, REVIEW_SPEC_DELAY_MS);
      });

      const result = await reviewSpec({
        openapi: specificationText,
        source,
      });

      setReviewSummary(result.summary);
      setFindings(result.findings);
      setCleanReviewedSpecificationText(result.summary.total === 0 ? specificationText : "");
      setReviewNotification(
        buildReviewSuccessNotification({
          summary: result.summary,
          findings: result.findings,
        })
      );
    } catch (error) {
      setCleanReviewedSpecificationText("");
      const message = normalizeReviewErrorMessage(error);
      setReviewNotification(
        createReviewNotification({
          status: "negative",
          title: "",
          description: message,
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApplyFixes = async () => {
    if (!canApplyFixes) {
      return;
    }

    setIsApplyingFixes(true);
    setReviewNotification(null);

    try {
      const previousSpecificationText = specificationText;
      const nextSpecificationText = await fixSpec({
        openapi: previousSpecificationText,
        source,
      });

      setSpecificationText(nextSpecificationText);
      setFixDiff({
        ...buildSpecDiff(previousSpecificationText, nextSpecificationText),
        beforeText: previousSpecificationText,
        afterText: nextSpecificationText,
      });
      setFindings([]);
      setActiveLine(0);
      setCleanReviewedSpecificationText("");
      setReviewSummary({
        total: 0,
        errors: 0,
        warnings: 0,
        info: 0,
      });
      setReviewNotification(
        createReviewNotification({
          status: "positive",
          title: "Fixes applied",
          description: "The specification editor was updated and the diff is shown beside it.",
        })
      );
    } catch (error) {
      const message = normalizeReviewErrorMessage(error);
      setReviewNotification(
        createReviewNotification({
          status: "negative",
          title: "",
          description: message,
        })
      );
    } finally {
      setIsApplyingFixes(false);
    }
  };

  const handleUploadToScg = async () => {
    if (!canUploadToScg) {
      return;
    }

    setIsUploadingToScg(true);
    setReviewNotification(null);
    setScgUploadResult(null);

    try {
      const result = await uploadSpecToScg({
        serviceName,
        openapi: specificationText,
      });
      setScgUploadResult(result);
    } catch (error) {
      const message = normalizeReviewErrorMessage(error);
      setReviewNotification(
        createReviewNotification({
          status: "negative",
          title: "",
          description: message,
        })
      );
    } finally {
      setIsUploadingToScg(false);
    }
  };

  const findingsContent = getFindingsContent({
    isSubmitting,
    visibleFindings,
    findings,
    emptyStateMessage,
    jumpToLine,
  });

  useEffect(() => {
    persistReviewState(typeof window === "undefined" ? null : window, {
      specificationText,
      cleanReviewedSpecificationText,
      source,
      searchValue,
      reviewSummary,
      findings,
      activeLine,
    });
    return undefined;
  }, [
    activeLine,
    cleanReviewedSpecificationText,
    findings,
    reviewSummary,
    searchValue,
    source,
    specificationText,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleStorage = (event) => {
      const nextState = parseReviewStorageEvent(event);

      if (!nextState) {
        return;
      }

      setSpecificationText(nextState.specificationText);
      setCleanReviewedSpecificationText(nextState.cleanReviewedSpecificationText);
      setSource(nextState.source);
      setSearchValue(nextState.searchValue);
      setReviewSummary(nextState.reviewSummary);
      setFindings(nextState.findings);
      setActiveLine(nextState.activeLine);
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return (
    <CDSContainer className="uag-review-page">
      {renderReviewNotification(reviewNotification)}

      <CDSFlex className="uag-page-heading" direction="vertical">
        <h1>Review spec</h1>
        <p>
          Upload or paste your existing OpenAPI file to receive readable findings and
          suggested fixes.
        </p>
      </CDSFlex>

      <section className="uag-review-grid">
        <CDSCard className="uag-review-input-card">
          <CDSFlex direction="vertical" gap="md">
            <div>
              <h2>Specification input</h2>
            </div>

            <CDSFlex className="uag-review-actions" gap="sm" align="center">
              <input
                ref={fileInputRef}
                type="file"
                aria-label="Specification file upload"
                accept=".yaml,.yml,.json,.txt"
                className="uag-review-file-input"
                onChange={handleUpload}
              />
              <CDSButton variant="secondary" onClick={handleOpenFilePicker}>
                Upload file
              </CDSButton>
              <CDSButton onClick={handleReview} disabled={isSubmitting}>
                {isSubmitting ? "Reviewing..." : "Review spec"}
              </CDSButton>
              <CDSButton
                className={`uag-apply-fixes-button${canApplyFixes ? " uag-apply-fixes-button--ready" : ""}`}
                onClick={handleApplyFixes}
                disabled={!canApplyFixes}
              >
                {isApplyingFixes ? "Applying..." : "Apply Fixes"}
              </CDSButton>
              <CDSButton
                className={`uag-upload-scg-button${canUploadToScg ? " uag-upload-scg-button--ready" : ""}`}
                onClick={handleUploadToScg}
                disabled={!canUploadToScg}
              >
                {isUploadingToScg ? "Uploading..." : "Onboard to SCG"}
              </CDSButton>
              <CDSButton variant="tertiary" onClick={handleClear} disabled={isSubmitting}>
                Clear
              </CDSButton>
            </CDSFlex>

            <div>
              <p id="review-spec-label" className="uag-form-label">
                OpenAPI specification
              </p>
              <div className={`uag-review-workspace${fixDiff ? " uag-review-workspace--split" : ""}`}>
                <div ref={editorShellRef} className="uag-review-editor-shell">
                  <div
                    ref={lineNumbersRef}
                    className="uag-review-line-numbers"
                    aria-hidden="true"
                  >
                    {Array.from({ length: specificationLines }, (_, index) => (
                      <span
                        key={`line-${index + 1}`}
                        className={activeLine === index + 1 ? "uag-review-line-number--active" : ""}
                      >
                        {index + 1}
                      </span>
                    ))}
                  </div>

                  <CDSTextArea
                    id="review-spec-input"
                    aria-label="OpenAPI specification"
                    aria-labelledby="review-spec-label"
                    width="auto"
                    value={specificationText}
                    onChange={handleSpecChange}
                    onScroll={handleSpecScroll}
                    onClick={(event) => syncActiveLineFromSelection(event.target)}
                    onKeyUp={(event) => syncActiveLineFromSelection(event.target)}
                    onSelect={(event) => syncActiveLineFromSelection(event.target)}
                    placeholder="Paste your OpenAPI YAML or JSON here"
                    rows={24}
                  />
                </div>

                {renderFixDiffPane({
                  fixDiff,
                  hasFixDiffChanges,
                  onDismiss: () => setFixDiff(null),
                })}
              </div>
            </div>
          </CDSFlex>
        </CDSCard>

        <CDSFlex direction="vertical" gap="md" className="uag-review-sidebar">
          <CDSCard className="uag-review-summary-card">
            <CDSFlex direction="vertical" gap="xs">
              <h2>Review summary</h2>
              {isSubmitting ? (
                <div className="uag-review-loading-block" aria-label="Review summary loading">
                  <CDSSkeleton height="md" lines={2} shade="regular" />
                </div>
              ) : (
                <CDSFlex className="uag-review-summary-row" gap="sm" wrap="wrap">
                  <span>{reviewSummary.total} total</span>
                  <span className="uag-review-summary-metric uag-review-summary-metric--error">
                    {reviewSummary.errors} errors
                  </span>
                  <span className="uag-review-summary-metric uag-review-summary-metric--warning">
                    {reviewSummary.warnings} warnings
                  </span>
                  <span className="uag-review-summary-metric uag-review-summary-metric--info">
                    {reviewSummary.info} info
                  </span>
                </CDSFlex>
              )}
            </CDSFlex>
          </CDSCard>

          <CDSCard className="uag-review-findings-card">
            <CDSFlex direction="vertical" gap="md">
              <div>
                <h2>Findings</h2>
              </div>

              <CDSTextInput
                id="review-search"
                label="Search findings"
                aria-label="Search findings"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search findings"
              />

              <CDSFlex direction="vertical" gap="sm">
                {findingsContent}
              </CDSFlex>
            </CDSFlex>
          </CDSCard>
        </CDSFlex>
      </section>

      {renderSubmittingOverlay(isSubmitting)}

      <CDSModal
        isOpen={Boolean(scgUploadResult)}
        onClose={() => {
          setScgUploadResult(null);
        }}
      >
        <CDSFlex direction="vertical" gap={16} className="uag-onboard-modal">
          <div>
            <h2>SCG upload created</h2>
            <p className="uag-section-copy">
              The spec upload completed and the GitHub pull request is ready.
            </p>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">GitHub PR</span>
            <a href={scgUploadResult?.pull_request_url} rel="noreferrer" target="_blank">
              {scgUploadResult?.pull_request_url}
            </a>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Env</span>
            <span>dev</span>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Region</span>
            <span>us</span>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Base branch</span>
            <span>{scgUploadResult?.base_branch}</span>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Branch</span>
            <span>{scgUploadResult?.branch}</span>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Spec path</span>
            <span>{scgUploadResult?.spec_path}</span>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Routes path</span>
            <span>{scgUploadResult?.routes_path}</span>
          </div>
          <div className="uag-onboard-modal-link-card">
            <span className="uag-onboard-modal-link-label">Routes generated</span>
            <span>{scgUploadResult?.routes_generated ? "Yes" : "No"}</span>
          </div>
          <CDSFlex gap={8}>
            <CDSButton
              onClick={() => {
                setScgUploadResult(null);
              }}
            >
              Close
            </CDSButton>
          </CDSFlex>
        </CDSFlex>
      </CDSModal>
    </CDSContainer>
  );
};

export const __testables = {
  buildSpecDiff,
  getStoredReviewState,
  getInitialReviewState,
  persistReviewState,
  parseReviewStorageEvent,
  extractServiceName,
  buildReviewSuccessNotification,
  normalizeReviewErrorMessage,
};

export default ReviewSpecPage;
